import React, { useEffect, useState } from "react";
import { useAppContext } from "../../app/AgTechAppContext";
import { AgTechRendering } from "../../components/AgTechRendering";
import { IAgTechAppActionSurfaceConfiguration } from "../../functions/AgTechAppLoading";
import { AgTechDataActionConfiguration, AgTechDataAction, AgTechDataActionExecutor, AgTechDataActionEventHandlers, AgTechDataActionContext, DefaultAgTechDataActionConfiguration, DataActionContext, useDataActionExecutor } from "./AgTechDataActions";
import { agtechDelay, executeAfter } from "../../utilities/AgTechUtilities";

export enum AgTechDataActionSurfaceStatus {
    Open,
    Submitting,
    Submitted
}

export type AgTechDataActionSurfaceState<TActionData, TResponseData> = {
    status: AgTechDataActionSurfaceStatus,
    confirmationData?: AgTechDataActionSurfaceConfirmationProps<TActionData, TResponseData>,
    actionConfiguration?: AgTechDataActionConfiguration,
    confirmationPane?: AgTechRendering
}

export type AgTechDataActionSurfaceProps<TActionData, TResponseData> = {
    action: AgTechDataAction<TActionData, TResponseData>,
    behavior?: AgTechDataActionSurfaceBehaviorProps<TActionData, TResponseData>,
    children: (dataActionExecutor: AgTechDataActionExecutor) => JSX.Element,
    classes?: string,
}

export type AgTechDataActionSurfaceConfirmationProps<TActionData, TResponseData> = {
    actionData: TActionData,
    responseData: TResponseData
}

export type AgTechDataActionSurfaceBehaviorProps<TActionData, TResponseData> = {
    returnToOpenStateOnCompletion?: boolean,
    showConfirmationPane?: boolean,
    eventHandlers?: AgTechDataActionEventHandlers<TActionData, TResponseData>,
    loading?: {
        loaderSize?: number
    },
    confirmation?: {
        renderActionConfirmationView?: (
            confirmationData: AgTechDataActionSurfaceConfirmationProps<TActionData, TResponseData>
        ) => Promise<React.ReactNode>
    }
}

const useDataActionSurface = <TActionData, TResponseData>(
    props: AgTechDataActionSurfaceProps<TActionData, TResponseData>
) => {
    const [surfaceState, updateSurfaceState] = useState<AgTechDataActionSurfaceState<TActionData, TResponseData>>({
        status: AgTechDataActionSurfaceStatus.Open
    });
    
    const actionSubmissionContext: AgTechDataActionContext<any, any> = {
        actionName: props.action.name,
        onExecutionStarted: async actionProps => {
            updateSurfaceState({
                status: AgTechDataActionSurfaceStatus.Submitting,
                actionConfiguration: actionProps.actionConfiguration
            });

            if (props.behavior?.eventHandlers?.onExecutionStarted) {
                await props.behavior.eventHandlers.onExecutionStarted(actionProps);
            }

            await agtechDelay(actionProps.actionConfiguration.actionExecutionDelay ?? 300);
        },
        onExecutionSuccessful: async actionProps => {
            if (props.behavior?.showConfirmationPane ?? true) {
                updateSurfaceState({
                    status: AgTechDataActionSurfaceStatus.Submitted,
                    actionConfiguration: actionProps.actionConfiguration,
                    confirmationData: {
                        actionData: actionProps.actionData,
                        responseData: actionProps.responseData,
                    }
                });
    
                await agtechDelay(actionProps.actionConfiguration.actionConfirmationDelay ?? 200);
            }

            if (props.behavior?.eventHandlers?.onExecutionSuccessful) {
                await props.behavior.eventHandlers.onExecutionSuccessful(actionProps);
            }
        },
        onExecutionCompleted: async result => {
            if (props.behavior?.eventHandlers?.onExecutionCompleted) {
                props.behavior?.eventHandlers?.onExecutionCompleted(result);
            }
            
            if (props.behavior?.returnToOpenStateOnCompletion ?? false) {
                updateSurfaceState({
                    status: AgTechDataActionSurfaceStatus.Open,
                });
            }
        },
        onExecutionFailed: async failureProps => {
            updateSurfaceState({
                status: AgTechDataActionSurfaceStatus.Open,
                actionConfiguration: failureProps.actionConfiguration
            });

            if (props.behavior?.eventHandlers?.onExecutionFailed) {
                await props.behavior.eventHandlers.onExecutionFailed(failureProps);
            }
        }
    }

    return {
        state: surfaceState,
        context: actionSubmissionContext
    };
}

const AgTechDataActionSurface = <TActionData, TResponseData>(props: AgTechDataActionSurfaceProps<TActionData, TResponseData>) => {
    const appContext = useAppContext();
    const dataActionSurface = useDataActionSurface(props);

    const actionSurfaceConfiguration = appContext.loading.actions;
    const actionConfiguration = dataActionSurface.state.actionConfiguration ?? DefaultAgTechDataActionConfiguration;

    const isSubmitting = dataActionSurface.state.status === AgTechDataActionSurfaceStatus.Submitting;
    const hasSubmitted = dataActionSurface.state.status === AgTechDataActionSurfaceStatus.Submitted;
    
    return (
        <DataActionContext.Provider value={dataActionSurface.context}>
            {actionSurfaceConfiguration.renderSurface({
                children: (
                    <>
                        <AgTechDataActionSurfaceBody
                            actionSurfaceConfiguration={actionSurfaceConfiguration}
                            isSubmitting={isSubmitting}
                            hasSubmitted={hasSubmitted}
                            children={props.children}
                        />
                        {isSubmitting && actionSurfaceConfiguration.renderActionSubmission({
                            submissionMessage: actionConfiguration.actionExecutionMessage ?? 'Submitting...',
                            loaderSize: props.behavior?.loading?.loaderSize
                        })}
                        {hasSubmitted && (
                            <AgTechDataActionSurfaceConfirmationView
                                {...props}
                                state={{...dataActionSurface.state}}
                            />
                        )}
                    </>
                )
            })}
        </DataActionContext.Provider> 
    )
}

const AgTechDataActionSurfaceBody = (props: {
    actionSurfaceConfiguration: IAgTechAppActionSurfaceConfiguration,
    isSubmitting: boolean,
    hasSubmitted: boolean,
    children: (dataActionExecutor: AgTechDataActionExecutor) => JSX.Element
}) => {
    const dataActionExecutor = useDataActionExecutor();
    const shouldShow = !props.isSubmitting && !props.hasSubmitted;

    return (
        <>
            {props.actionSurfaceConfiguration.renderSurfaceBody({
                isOpen: shouldShow,
                children: props.children(dataActionExecutor)
            })}
        </>
    )
}

const AgTechDataActionSurfaceConfirmationView = <TActionData, TResponseData>(
    props: AgTechDataActionSurfaceProps<TActionData, TResponseData> & {
        state: AgTechDataActionSurfaceState<TActionData, TResponseData>
    }
) => {
    const appContext = useAppContext();
    const hasCustomConfirmationView = props.behavior?.confirmation?.renderActionConfirmationView && props.state.confirmationData;
    const actionConfiguration = props.state.actionConfiguration ?? DefaultAgTechDataActionConfiguration;

    const [customConfirmationView, setConfirmationView] = useState<React.ReactNode>(null);

    useEffect(() => {
        if (hasCustomConfirmationView) {
            // Give time for the action confirmation to show before loading any custom confirmation pane
            executeAfter({
                action: () => {
                    setConfirmationView(<></>);
                    return props.behavior!.confirmation!.renderActionConfirmationView!(props.state.confirmationData!);
                },
                delay: 600,
                onComplete: async customConfirmationView => {
                    if (customConfirmationView) {
                        setConfirmationView(customConfirmationView);
                    }
                }
            });
        }
    }, []);

    return (
        <>
            {customConfirmationView ?? appContext.loading.actions.renderActionConfirmation({
                confirmationMessage: actionConfiguration.actionConfirmationMessage ?? 'Success!',
                confirmationDetails: actionConfiguration.actionConfirmationDetails
            })}
        </>
    )
}

export default React.memo(AgTechDataActionSurface) as typeof AgTechDataActionSurface;